import {Injectable} from '@angular/core';
import {Currency} from '../objects/Currency';
import {BehaviorSubject, Observable} from 'rxjs';
import {CurrencyRates} from '../types/CurrencyRates';
import {filter, map} from 'rxjs/operators';
import {SocketNamespaceServiceBase} from '~local/app/core/socket/SocketNamespaceServiceBase';

export function updateCurrencyFrom($currencies: Array<Currency>): Array<Currency> {
    return $currencies.map($currency => {
        return {
            ...$currency,
            from: $currencies
                .filter($ => $.code !== $currency.code && $.to.indexOf($currency.code) > -1)
                .map($ => $.code),
        };
    });
}

export type CurrencyResponse = {
    code: string,
    codeLabel: string,
    name: string,
    protocol: string,
    symbol: string,
    type: 'crypto' | 'fiat' | 'internal',
    rates?: CurrencyRates,
};

@Injectable()
export class CurrencyService extends SocketNamespaceServiceBase {

    protected readonly _currencies$ = new BehaviorSubject<Array<Currency> | null>(null);
    protected default = 'BTC';

    constructor(
    ) {
        super('currency');
    }

    private getData() {
        this.socket.once('dataR', (data: { currencies: Array<CurrencyResponse>, default: string }) => {
            if (!data) {
                this._currencies$.next([]);
                return;
            }

            if (data.default) {
                this.default = data.default;
            }

            const newCurrencies = data.currencies.map($responseCurrency => {
                if ($responseCurrency.rates && Object.keys($responseCurrency.rates).length > 0) {
                    return {
                        ...$responseCurrency,
                        rates: {},
                        to: Object.keys($responseCurrency.rates),
                        from: [],
                    };
                }

                return {
                    ...$responseCurrency,
                    rates: {},
                    to: [],
                    from: [],
                };
            });

            this._currencies$.next(updateCurrencyFrom(newCurrencies));
        });
        this.socket.emit('data');
    }

    protected onConnect() {
        this.getData();
    }

    // public getters
    public get all(): Currency[] {
        return this._currencies$.value || [];
    }

    public currency(code: string = this.default): Currency | null {
        return this._currencies$.value && (this._currencies$.value.find(c => c.code === code.toUpperCase()) || null);
    }

    public getByType(type: string): Array<Currency> {
        return (this._currencies$.value || []).filter(c => c.type === type.toLowerCase());
    }

    protected initListeners() {
        this.socket.on('rate', ($code: string, $rates: CurrencyRates) => {
            const loadedCurrencyIndex = this._currencies$.value!.findIndex($ => $.code === $code);
            if (loadedCurrencyIndex > -1) {
                this._currencies$.next(updateCurrencyFrom([
                    ...this._currencies$.value!.slice(0, loadedCurrencyIndex),
                    {
                        ...this._currencies$.value![loadedCurrencyIndex],
                        rates: $rates,
                    },
                    ...this._currencies$.value!.slice(loadedCurrencyIndex + 1),
                ]));
            }
        });
    }

    public get currencies$(): Observable<Array<Currency> | null> {
        return this._currencies$;
    }

    public get fiatCurrencies$(): Observable<Array<Currency>> {
        return this.currencies$.pipe(
            filter(($): $ is Array<Currency> => Array.isArray($)),
            map($currencies => $currencies.filter($ => $.type === 'fiat')),
        );
    }
}
